import * as Dialog from '@cannect/new-components/atoms/Dialog'
import { X } from 'lucide-react'
import { useMemo } from 'react'
import { TERMS_SUBTITLE, TERMS_TEXT, TERMS_TITLE, TERMS_VERSION } from './terms'
import { TrailCta } from './TrailShell'

type TBlock = { type: 'section'; title: string } | { type: 'p'; text: string } | { type: 'ul'; items: string[] }

// Formato de terms.ts: "## " abre uma seção, linhas "- " viram lista e o resto são parágrafos.
const parseTerms = (text: string): TBlock[] =>
  text
    .trim()
    .split(/\n\s*\n/)
    .map((chunk) => chunk.split('\n').map((line) => line.trim()))
    .map((lines): TBlock => {
      if (lines.length === 1 && lines[0].startsWith('## ')) return { type: 'section', title: lines[0].slice(3) }
      if (lines.every((line) => line.startsWith('- '))) return { type: 'ul', items: lines.map((line) => line.slice(2)) }
      return { type: 'p', text: lines.join(' ') }
    })

// Termos do programa em modal (sem página própria), por cima do modal da trilha.
export const TermsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const blocks = useMemo(() => parseTerms(TERMS_TEXT), [])

  return (
    <Dialog.Root open={open} onOpenChange={(value) => !value && onClose()}>
      <Dialog.Content
        hideClose
        overlayClassName="z-[1000003] bg-[rgba(26,26,26,0.55)]"
        className="left-0 top-0 z-[1000003] flex h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-white p-0 shadow-none md:left-1/2 md:top-1/2 md:h-auto md:max-h-[88dvh] md:w-[min(calc(100%-48px),720px)] md:max-w-[720px] md:translate-x-[-50%] md:translate-y-[-50%] md:rounded-[24px] md:shadow-[0_24px_80px_rgba(42,47,29,0.28)]">
        <header className="flex shrink-0 items-start justify-between gap-3 border-0 border-b border-solid border-[#EEEEEE] px-5 py-4 md:px-6">
          <div>
            <Dialog.Title className="m-0 text-lg font-bold leading-tight text-[#1A1A1A]">{TERMS_TITLE}</Dialog.Title>
            <Dialog.Description className="m-0 mt-0.5 text-[13px] text-[#6B6560]">
              {TERMS_SUBTITLE} · versão {TERMS_VERSION}
            </Dialog.Description>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar termos"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border-0 bg-transparent text-[#1A1A1A]">
            <X className="size-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-sm leading-[1.6] text-[#433F3E] md:px-6">
          {blocks.map((block, index) => {
            if (block.type === 'section') {
              return (
                <h3 key={block.title} className="m-0 mb-2 mt-6 text-[13px] font-bold tracking-[0.3px] text-[#1A1A1A]">
                  {block.title}
                </h3>
              )
            }
            if (block.type === 'ul') {
              return (
                <ul key={`ul-${index}`} className="my-3 list-disc space-y-1 pl-5">
                  {block.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )
            }
            return (
              <p key={`p-${index}`} className="m-0 mb-3">
                {block.text}
              </p>
            )
          })}
        </div>

        <footer className="shrink-0 border-0 border-t border-solid border-[#EEEEEE] bg-white px-5 pb-5 pt-3 md:px-6">
          <TrailCta onClick={onClose}>Fechar</TrailCta>
        </footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}
